import { motion } from 'framer-motion'
import { Helmet } from 'react-helmet-async'

const Privacy = () => {
    return (
        <>
            <Helmet>
                <title>Privacy Policy | Margdarshak</title>
                <meta name="description" content="Read Margdarshak's privacy policy to understand how we collect, use, and protect your personal information." />
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            <div className="min-h-screen bg-gray-50 pt-20">
                {/* Hero Section */}
                <section className="bg-gradient-to-br from-primary-600 via-secondary-600 to-primary-800 text-white py-16 relative overflow-hidden">
                    {/* Background Pattern */}
                    <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>

                    <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="text-center"
                        >
                            <div className="inline-flex items-center justify-center w-20 h-20 bg-accent-100/20 backdrop-blur-sm rounded-full mb-8">
                                <svg className="w-10 h-10 text-accent-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                            </div>

                            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white drop-shadow-lg">
                                <span className="text-accent-400">Privacy</span> <span className="text-gold-400">Policy</span>
                            </h1>
                            <p className="text-xl sm:text-2xl mb-8 max-w-3xl mx-auto text-white drop-shadow-md">
                                Your privacy is important to us. Learn how we protect your information.
                            </p>
                        </motion.div>
                    </div>
                </section>

                {/* Content Section */}
                <section className="py-16">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="card p-8"
                        >
                            <div className="prose prose-lg max-w-none">
                                <p className="text-sm text-gray-500 mb-8">
                                    Last updated: {new Date().toLocaleDateString()}
                                </p>

                                <h2>1. Information We Collect</h2>
                                <p>
                                    We collect information you provide directly to us, such as when you create an account,
                                    submit an enquiry, or contact us for support.
                                </p>

                                <h3>Personal Information</h3>
                                <ul>
                                    <li>Name and contact information</li>
                                    <li>Email address and phone number</li>
                                    <li>Educational background and interests</li>
                                    <li>Communication preferences</li>
                                </ul>

                                <h3>Automatically Collected Information</h3>
                                <ul>
                                    <li>Device and browser information</li>
                                    <li>IP address and location data</li>
                                    <li>Usage patterns and preferences</li>
                                    <li>Cookies and similar technologies</li>
                                </ul>

                                <h2>2. How We Use Your Information</h2>
                                <p>We use the information we collect to:</p>
                                <ul>
                                    <li>Provide and improve our services</li>
                                    <li>Respond to your enquiries and requests</li>
                                    <li>Send you relevant information about courses and programs</li>
                                    <li>Analyze usage patterns to enhance user experience</li>
                                    <li>Comply with legal obligations</li>
                                </ul>

                                <h2>3. Information Sharing</h2>
                                <p>
                                    We do not sell, trade, or otherwise transfer your personal information to third parties
                                    without your consent, except as described in this policy.
                                </p>

                                <h3>We may share information with:</h3>
                                <ul>
                                    <li>Partner universities and educational institutions</li>
                                    <li>Service providers who assist in our operations</li>
                                    <li>Legal authorities when required by law</li>
                                    <li>Business partners with your explicit consent</li>
                                </ul>

                                <h2>4. Data Security</h2>
                                <p>
                                    We implement appropriate security measures to protect your personal information against
                                    unauthorized access, alteration, disclosure, or destruction.
                                </p>

                                <h2>5. Your Rights</h2>
                                <p>You have the right to:</p>
                                <ul>
                                    <li>Access and update your personal information</li>
                                    <li>Request deletion of your data</li>
                                    <li>Opt-out of marketing communications</li>
                                    <li>Request data portability</li>
                                </ul>

                                <h2>6. Cookies and Tracking</h2>
                                <p>
                                    We use cookies and similar technologies to enhance your browsing experience and
                                    analyze website traffic. You can control cookie settings through your browser.
                                </p>

                                <h2>7. Third-Party Links</h2>
                                <p>
                                    Our website may contain links to third-party websites. We are not responsible for
                                    the privacy practices of these external sites.
                                </p>

                                <h2>8. Children's Privacy</h2>
                                <p>
                                    Our services are not intended for children under 13. We do not knowingly collect
                                    personal information from children under 13.
                                </p>

                                <h2>9. Changes to This Policy</h2>
                                <p>
                                    We may update this privacy policy from time to time. We will notify you of any
                                    material changes by posting the new policy on this page.
                                </p>

                                <h2>10. Contact Us</h2>
                                <p>
                                    If you have any questions about this privacy policy, please contact us at:
                                </p>
                                <ul>
                                    <li>Email: privacy@margdarshak.com</li>
                                    <li>Phone: +91-9810530354</li>
                                    <li>Address: Lal Kuah, Ghaziabad, Uttar Pradesh, India</li>
                                </ul>
                            </div>
                        </motion.div>
                    </div>
                </section>
            </div>
        </>
    )
}

export default Privacy
